import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Card, StatCard, Badge, Spinner, Empty } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { TierBadge, TierCard } from '../../components/ui/TierBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLinkWallet } from '../../hooks/useLinkWallet';
import { referralApi, type ReferralSummary, type Referee, type MyPayout } from '../../api/referral';
import { walletApi } from '../../api/wallet';
import { fmtUsdt, fmtAiml, fmtDate, shortenAddress, copyToClipboard } from '../../lib/format';

interface LinkedWallet {
  id: string;
  address: string;
  linkedAt: string;
}

// Tier thresholds — keep in sync with backend lib/affiliateTiers.ts
const BRONZE_THRESHOLD = 100;
const SILVER_THRESHOLD = 300;
const FOUNDERS_TEAM_THRESHOLD = 3_000;

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const toast = useToast();
  const { address: connectedAddress, isConnected } = useAccount();
  const { linking, link: doLink } = useLinkWallet();

  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [referees, setReferees] = useState<Referee[] | null>(null);
  const [wallets, setWallets] = useState<LinkedWallet[] | null>(null);
  const [payouts, setPayouts] = useState<MyPayout[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [s, r, w, p] = await Promise.all([
        referralApi.me(),
        referralApi.referees(),
        walletApi.get(),
        referralApi.payouts(),
      ]);
      setSummary(s);
      setReferees(r.referees);
      setWallets(w.wallets);
      setPayouts(p.payouts);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCopyLink = async () => {
    if (!summary?.referralUrl) return;
    const ok = await copyToClipboard(summary.referralUrl);
    if (ok) toast.success('Referral link copied');
    else toast.error('Could not copy. Select and copy manually.');
  };

  const handleLinkWallet = async () => {
    const result = await doLink();
    if (result.ok) {
      toast.success('Wallet linked');
      await refresh();
    } else {
      // Show the real reason (e.g. "You rejected the signature", "Signature
      // request timed out", or a server error message) — much more useful
      // than the previous generic "Wallet linking failed" toast.
      toast.error(result.error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="max-w-2xl mx-auto py-10">
        <Card>
          <div className="text-danger font-display text-lg">Could not load dashboard</div>
          <div className="text-ink-600 text-sm mt-2">{error}</div>
          <Button onClick={refresh} variant="outline" className="mt-4">
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  const hasWallet = (wallets?.length ?? 0) > 0;
  const linkedAddress = wallets?.[0]?.address ?? null;
  const kycApproved = summary.kycStatus === 'APPROVED';

  const tier = summary.tier;
  const isAffiliate = tier.current !== 'NONE';
  const teamVolume = summary.spend.teamVolumeUsdt ?? summary.spend.directTeamVolumeUsdt;
  const personalSpend = summary.spend.personalUsdt;

  // L2 (indirect) earnings — Bronze/Silver get 2%, Founders get 3% of every
  // indirect sale, both in USDT and AIML. Backend exposes the total under
  // `totalIndirectUsdt` (also kept as legacy alias `totalLeadershipOverrideEarnedUsdt`).
  const indirectUsdt =
    summary.earnings.totalIndirectUsdt ?? summary.earnings.totalLeadershipOverrideEarnedUsdt;
  const directUsdt = Math.max(0, summary.earnings.totalCommissionEarnedUsdt - indirectUsdt);

  // Progress bar to next tier (new thresholds: $100 / $300 / $3,000 team)
  let progressPercent = 0;
  let progressLabel = '';
  if (tier.next === 'BRONZE') {
    progressPercent = Math.min(100, (personalSpend / BRONZE_THRESHOLD) * 100);
    progressLabel = `$${fmtUsdt(personalSpend)} of $${BRONZE_THRESHOLD} personal spend`;
  } else if (tier.next === 'SILVER') {
    progressPercent = Math.min(
      100,
      ((personalSpend - BRONZE_THRESHOLD) / (SILVER_THRESHOLD - BRONZE_THRESHOLD)) * 100,
    );
    progressLabel = `$${fmtUsdt(personalSpend)} of $${SILVER_THRESHOLD} personal spend`;
  } else if (tier.next === 'FOUNDERS_CIRCLE') {
    progressPercent = Math.min(100, (teamVolume / FOUNDERS_TEAM_THRESHOLD) * 100);
    progressLabel = `$${fmtUsdt(teamVolume)} of $${fmtUsdt(FOUNDERS_TEAM_THRESHOLD)} team volume`;
  }

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-violet-600 mb-2">Affiliate dashboard</div>
          <h1 className="font-display text-4xl font-bold">Welcome, {user?.email}</h1>
          <p className="text-ink-600 mt-2">
            Your referral code:{' '}
            <span className="font-mono font-semibold text-ink">{summary.referralCode}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TierBadge tier={tier.current} size="md" />
          <Badge kind={kycApproved ? 'success' : 'warn'}>KYC: {summary.kycStatus}</Badge>
        </div>
      </div>

      {/* Tier card with L1 rates + L2 rates + progress to next tier */}
      <div className="mb-6">
        <TierCard
          tier={tier.current}
          usdtPercent={tier.rates.usdtPercent}
          aimlPercent={tier.rates.aimlPercent}
          leadershipOverridePercent={tier.rates.leadershipOverridePercent}
        >
          {/* Level 2 (indirect) rates — visible for everyone who's reached Bronze */}
          {isAffiliate && (
            <div className="mt-4 pt-4 border-t border-paper-200">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-ink-600 uppercase tracking-wider">
                  Level 2 (indirect) earnings
                  {tier.current === 'FOUNDERS_CIRCLE' && (
                    <span className="ml-2 text-violet-700 italic normal-case">leadership tier</span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div>
                  <span className="font-display text-2xl font-bold text-ink">
                    {tier.rates.l2UsdtPercent}%
                  </span>
                  <span className="text-xs text-ink-600 ml-2 uppercase tracking-wider">
                    USDT
                  </span>
                </div>
                <div>
                  <span className="font-display text-2xl font-bold text-ink">
                    {tier.rates.l2AimlPercent}%
                  </span>
                  <span className="text-xs text-ink-600 ml-2 uppercase tracking-wider">
                    AIML
                  </span>
                </div>
              </div>
              <p className="text-xs text-ink-600 mt-2">
                You earn passively every time someone in your team's downline (your
                referrals' referrals) buys $AIML — no recruiting effort needed.
              </p>
            </div>
          )}

          {/* Progress to next tier */}
          {tier.next && (
            <div className="mt-4 pt-4 border-t border-paper-200">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs text-ink-600 uppercase tracking-wider">
                  Progress to {tier.nextLabel}
                </div>
                <div className="text-xs font-mono text-ink-700">
                  {Math.floor(progressPercent)}%
                </div>
              </div>
              <div className="h-2 bg-paper-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-magenta-500 via-violet-500 to-azure-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="text-xs text-ink-600 mt-2">{progressLabel}</div>
            </div>
          )}
        </TierCard>
      </div>

      {/* Not-yet-affiliate banner */}
      {!isAffiliate && (
        <Card className="mb-6 !bg-violet-50 !border-violet-200">
          <div className="flex items-start gap-4">
            <div className="text-3xl">🎯</div>
            <div className="flex-1">
              <h3 className="font-display font-bold text-lg">
                Become a Bronze Affiliate
              </h3>
              <p className="text-ink-700 text-sm mt-1">
                Buy at least <strong>$100 of AIML</strong> to unlock your referral link.
                Bronze pays <strong>10% USDT + 2% AIML</strong> on every friend you bring,
                plus a passive <strong>2% USDT + 2% AIML</strong> on every sale your team's
                referrals make (Level 2).
              </p>
              <div className="mt-4">
                <Link to="/buy">
                  <Button variant="gradient" size="sm">
                    Buy $AIML now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Referral link */}
      {isAffiliate && summary.referralUrl && (
        <Card className="mb-6">
          <div className="eyebrow mb-3">Your referral link</div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0 font-mono text-sm bg-paper-100 px-4 py-3 rounded-2xl border border-paper-300 truncate">
              {summary.referralUrl}
            </div>
            <Button onClick={handleCopyLink} variant="primary">
              Copy link
            </Button>
          </div>
          <p className="text-xs text-ink-600 mt-3">
            Share this link. When someone buys $AIML through it, you earn{' '}
            <strong>{tier.rates.usdtPercent}% USDT + {tier.rates.aimlPercent}% AIML</strong>{' '}
            on the direct sale, plus a passive{' '}
            <strong>{tier.rates.l2UsdtPercent}% USDT + {tier.rates.l2AimlPercent}% AIML</strong>{' '}
            on every sale they refer downstream — all sent straight to your wallet (after KYC).
          </p>
        </Card>
      )}

      {/* Top stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Clicks"
          value={summary.stats.totalClicks}
          accent="azure"
          hint="Total link clicks"
        />
        <StatCard
          label="Signups"
          value={summary.stats.totalSignups}
          accent="violet"
          hint="People who joined"
        />
        <StatCard
          label="Qualifying refs"
          value={summary.stats.qualifyingReferrals}
          accent="magenta"
          hint="Referees who bought"
        />
        <StatCard
          label="Payable USDT"
          value={`$${fmtUsdt(summary.earnings.payableUsdt)}`}
          accent="ink"
          hint={
            kycApproved && hasWallet
              ? 'Will be paid in next batch'
              : 'Locked until KYC + wallet'
          }
        />
      </div>

      {/* Spend & wallet */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-bold mb-3">Spend & wallet</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <div className="eyebrow mb-2">Total spent</div>
            <div className="stat-num text-ink">${fmtUsdt(personalSpend)}</div>
            <div className="text-xs text-ink-600 mt-1">Your AIML purchases (USD)</div>
          </Card>
          <Card>
            <div className="eyebrow mb-2">AIML purchased</div>
            <div className="stat-num text-ink">{fmtAiml(summary.incentives?.aimlPurchased ?? 0)}</div>
            <div className="text-xs text-ink-600 mt-1">Via your linked wallet</div>
          </Card>
          <Card>
            <div className="eyebrow mb-2">Account credit</div>
            <div className="stat-num text-ink">${fmtUsdt(summary.earnings.payableUsdt)}</div>
            <div className="text-xs text-ink-600 mt-1">Commission earned, awaiting payout</div>
          </Card>
          <Card>
            <div className="eyebrow mb-2">AIML rewards earned</div>
            <div className="stat-num text-ink">{fmtAiml(summary.incentives?.aimlEarned ?? 0)}</div>
            <div className="text-xs text-ink-600 mt-1">Incentive tokens</div>
          </Card>
        </div>
      </div>

      {/* Earnings breakdown — Total / L1 Direct / L2 Indirect / Paid */}
      <div className="mb-6">
        <h2 className="font-display text-xl font-bold mb-3">Earnings breakdown</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <div className="eyebrow mb-2">Total earned</div>
            <div className="stat-num text-ink">
              ${fmtUsdt(summary.earnings.totalCommissionEarnedUsdt)}
            </div>
            <div className="text-xs text-ink-600 mt-1">Lifetime USDT commissions</div>
          </Card>

          <Card>
            <div className="eyebrow mb-2 flex items-center gap-1">
              <span>🎯</span>
              <span>Direct (L1)</span>
            </div>
            <div className="stat-num text-ink">
              ${fmtUsdt(directUsdt)}
            </div>
            <div className="text-xs text-ink-600 mt-1">
              Active — from referrals you brought
            </div>
          </Card>

          <Card className="!border-violet-200 !bg-gradient-to-br from-violet-50 to-magenta-50">
            <div className="eyebrow mb-2 text-violet-700 flex items-center gap-1">
              <span>🌱</span>
              <span>Indirect (L2)</span>
            </div>
            <div className="stat-num text-gradient">
              ${fmtUsdt(indirectUsdt)}
            </div>
            <div className="text-xs text-ink-700 mt-1">
              Passive — from your team's referrals
            </div>
          </Card>

          <Card>
            <div className="eyebrow mb-2">Already paid</div>
            <div className="stat-num text-success">
              ${fmtUsdt(summary.earnings.totalCommissionPaidUsdt)}
            </div>
            <div className="text-xs text-ink-600 mt-1">Sent to your wallet</div>
          </Card>
        </div>

        {/* Encouragement banner for users earning only L1 so far */}
        {isAffiliate && indirectUsdt === 0 && directUsdt > 0 && (
          <div className="mt-4 p-4 rounded-2xl bg-azure-50 border border-azure-200 flex items-start gap-3 text-sm">
            <span className="text-xl">💡</span>
            <div className="flex-1 text-ink-700">
              <strong>Tip:</strong> Coach your referrals to recruit too. Every sale they
              bring in pays you a passive{' '}
              <strong>
                {tier.rates.l2UsdtPercent}% USDT + {tier.rates.l2AimlPercent}% AIML
              </strong>
              {' '}— forever, with no extra effort from you.
            </div>
          </div>
        )}
      </div>

      {/* KYC + wallet status row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card>
          <div className="flex items-start justify-between mb-3">
            <div className="eyebrow">KYC verification</div>
            <Badge
              kind={
                summary.kycStatus === 'APPROVED'
                  ? 'success'
                  : summary.kycStatus === 'PENDING'
                    ? 'warn'
                    : summary.kycStatus === 'REJECTED'
                      ? 'danger'
                      : 'neutral'
              }
            >
              {summary.kycStatus}
            </Badge>
          </div>
          {summary.kycStatus === 'NONE' && (
            <>
              <p className="text-ink-700 text-sm">
                You must complete KYC before any commissions can be paid out.
              </p>
              <Link to="/dashboard/kyc" className="inline-block mt-4">
                <Button variant="primary" size="sm">
                  Start KYC
                </Button>
              </Link>
            </>
          )}
          {summary.kycStatus === 'PENDING' && (
            <p className="text-ink-700 text-sm">
              Your KYC is under review. We'll email you when it's decided.
            </p>
          )}
          {summary.kycStatus === 'APPROVED' && (
            <p className="text-success text-sm">✓ KYC approved. Payouts can be processed.</p>
          )}
          {summary.kycStatus === 'REJECTED' && (
            <>
              <p className="text-danger text-sm">
                Your KYC was rejected. You may resubmit.
              </p>
              <Link to="/dashboard/kyc" className="inline-block mt-4">
                <Button variant="primary" size="sm">
                  Resubmit KYC
                </Button>
              </Link>
            </>
          )}
        </Card>

        <Card>
          <div className="flex items-start justify-between mb-3">
            <div className="eyebrow">Payout wallet</div>
            <Badge kind={hasWallet ? 'success' : 'warn'}>
              {hasWallet ? 'Linked' : 'Not linked'}
            </Badge>
          </div>

          {hasWallet ? (
            <>
              <p className="text-ink-700 text-sm">Payouts will be sent to:</p>
              <div className="font-mono text-sm mt-2 bg-paper-100 px-3 py-2 rounded-xl">
                {linkedAddress}
              </div>
              <p className="text-xs text-ink-600 mt-2">
                Linked {wallets?.[0] && fmtDate(wallets[0].linkedAt)}
              </p>
            </>
          ) : (
            <>
              <p className="text-ink-700 text-sm">
                Link a wallet so we know where to send your USDT commissions.
              </p>
              {!isConnected ? (
                <div className="mt-4">
                  <ConnectButton showBalance={false} chainStatus="none" />
                </div>
              ) : (
                <>
                  <div className="text-xs text-ink-600 mt-3">
                    Connected: <span className="font-mono">{shortenAddress(connectedAddress)}</span>
                  </div>
                  <Button
                    onClick={handleLinkWallet}
                    isLoading={linking}
                    variant="primary"
                    size="sm"
                    className="mt-4"
                  >
                    Sign & link this wallet
                  </Button>
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Referees table */}
      <div className="mb-4">
        <h2 className="font-display text-2xl font-bold">Your direct referrals</h2>
        <p className="text-ink-600 text-sm mt-1">
          People who signed up using your code. (Their referrals appear in your Level 2
          earnings once they bring people in.)
        </p>
      </div>

      {!referees || referees.length === 0 ? (
        <Empty
          title="No referrals yet"
          hint="Share your referral link to start earning"
        />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper-100">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Email</th>
                  <th className="px-4 py-3 eyebrow">Joined</th>
                  <th className="px-4 py-3 eyebrow">KYC</th>
                  <th className="px-4 py-3 eyebrow text-right">Purchases</th>
                  <th className="px-4 py-3 eyebrow text-right">Total spent</th>
                  <th className="px-4 py-3 eyebrow text-right">You earned</th>
                </tr>
              </thead>
              <tbody>
                {referees.map((r) => (
                  <tr key={r.id} className="border-t border-paper-200">
                    <td className="px-4 py-3 font-mono text-xs">{r.email}</td>
                    <td className="px-4 py-3 text-ink-700">{fmtDate(r.joinedAt)}</td>
                    <td className="px-4 py-3">
                      <Badge
                        kind={
                          r.kycStatus === 'APPROVED'
                            ? 'success'
                            : r.kycStatus === 'PENDING'
                              ? 'warn'
                              : 'neutral'
                        }
                      >
                        {r.kycStatus}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {r.qualifyingPurchases}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${fmtUsdt(r.totalPurchasedUsdt)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-success">
                      ${fmtUsdt(r.youEarnedUsdt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Payout history & proof */}
      <div className="mt-10 mb-4">
        <h2 className="font-display text-2xl font-bold">Payout history</h2>
        <p className="text-ink-600 text-sm mt-1">
          Every payout we send is recorded here with its on-chain transaction hash as proof
          of payment.
        </p>
      </div>
      {!payouts || payouts.length === 0 ? (
        <Empty
          title="No payouts yet"
          hint="Once your KYC is approved, your weekly commission is paid out here."
        />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper-100">
                <tr className="text-left">
                  <th className="px-4 py-3 eyebrow">Date</th>
                  <th className="px-4 py-3 eyebrow text-right">Amount</th>
                  <th className="px-4 py-3 eyebrow text-right">Lines</th>
                  <th className="px-4 py-3 eyebrow">Status</th>
                  <th className="px-4 py-3 eyebrow">Proof (tx hash)</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-t border-paper-200">
                    <td className="px-4 py-3 text-ink-700">{fmtDate(p.createdAt)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-success">
                      ${fmtUsdt(p.usdtAmount)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{p.commissionCount}</td>
                    <td className="px-4 py-3">
                      <Badge kind={p.confirmedAt ? 'success' : 'info'}>
                        {p.confirmedAt ? 'Confirmed' : 'Sent'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <a
                        href={`https://polygonscan.com/tx/${p.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-600 hover:underline"
                      >
                        {shortenAddress(p.txHash, 10, 8)} →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-10 text-center text-xs text-ink-600">
        <p>
          Need help? Reach out at{' '}
          <a href="mailto:support@altcoinsmoney.com" className="text-violet-600 hover:underline">
            support@altcoinsmoney.com
          </a>
        </p>
      </div>
    </div>
  );
}